/**
 * Who won a fight that nobody finished.
 *
 * Until now the only way out of a round was somebody going out, so two fighters
 * who never connected circled forever — and until step 48 they did. A time
 * limit without a rule for reading the bars only moves the problem: the round
 * ends and nobody knows what happened.
 *
 * So the criteria are **explicit, ordered and in data**. `match.json` names them
 * and this walks them:
 *
 *   wellness       the body. What has actually been taken off somebody, and the
 *                  only bar that never comes back — so it is the first question
 *                  and usually the last one.
 *   consciousness  how close they came to going out. It regenerates, so two
 *                  fighters level on wellness are separated by who was in more
 *                  trouble *recently*.
 *   a draw         when neither margin is clear. Not a failure to decide; a
 *                  statement that there was nothing in it.
 *
 * A margin rather than a comparison, because `92.4 > 92.39` is not a win that
 * anybody watching would accept — and a bar read to the tenth of a point is a
 * verdict decided by floating-point noise.
 *
 * Pure. It is handed two health-shaped things and returns a verdict.
 */

export class DecisionConfigError extends Error {}

/** The bars that can decide a fight, in the order they are asked. */
export const CRITERIA = Object.freeze(['wellness', 'consciousness']);

export function validateDecision(config) {
  const order = config?.order;
  if (!Array.isArray(order) || order.length === 0) {
    throw new DecisionConfigError('a decision needs at least one criterion to read');
  }
  for (const name of order) {
    if (!CRITERIA.includes(name)) {
      throw new DecisionConfigError(
        `"${name}" is not something a fighter has; there is ${CRITERIA.join(', ')}`
      );
    }
    const margin = config.margins?.[name];
    // A margin of zero makes the criterion a raw comparison, which decides a
    // title on a rounding error. Every criterion has to declare how much
    // counts as "ahead".
    if (!(margin > 0)) {
      throw new DecisionConfigError(
        `"${name}" has a margin of ${JSON.stringify(margin)}; want more than zero`
      );
    }
  }
  if (new Set(order).size !== order.length) {
    throw new DecisionConfigError('a decision reads each criterion once');
  }
  return config;
}

/**
 * Reads the bars and says who it went to.
 *
 * `fighters` is a pair. Returns the same shape whatever happens, so a caller
 * never has to ask which kind of answer it got:
 *
 *   { verdict: 'decision', winner, loser, on: 'wellness', margin: 11.4 }
 *   { verdict: 'draw', winner: null, loser: null, on: null, margin: 0 }
 */
export function decide(fighters = [], config) {
  validateDecision(config);
  const [a, b] = fighters;
  if (!a || !b) return Object.freeze({ verdict: 'draw', winner: null, loser: null, on: null, margin: 0 });

  for (const name of config.order) {
    const mine = Number(a.health?.[name]) || 0;
    const theirs = Number(b.health?.[name]) || 0;
    const margin = Math.abs(mine - theirs);
    if (margin <= config.margins[name]) continue;
    const ahead = mine > theirs ? a : b;
    const behind = ahead === a ? b : a;
    return Object.freeze({
      verdict: 'decision',
      winner: ahead.id ?? null,
      loser: behind.id ?? null,
      on: name,
      margin,
    });
  }

  return Object.freeze({ verdict: 'draw', winner: null, loser: null, on: null, margin: 0 });
}
